import axios from 'axios';
import { logProtocol, logProtocolError } from './traceabilityLog';

/**
 * El servidor que reparte los números de producción de las placas.
 *
 * Es el reemplazo de la planilla de seriales que hoy maneja a mano la herramienta
 * de manufactura (CODE/Edupower_Serial_EN.py en el repo de trazabilidad): una
 * placa sin número pide uno, lo graba en su EEPROM, y avisa para que quede
 * marcado como ocupado.
 *
 * El número llega armado. Acá no se construye ni se completa nada: lo que el
 * servidor manda es lo que se graba, textual. La forma la valida el que escribe
 * -- ver writeSerialString en traceabilityLink, que es quien conoce lo que la
 * placa acepta -- y si no sirve, el número se devuelve con releaseSerialString en
 * vez de quemarse.
 */

/**
 * Los endpoints todavía no existen: esto es la propuesta que se va a llevar a
 * quien los desarrolle, y las dos URLs son provisorias. Se cambian acá o se
 * pisan por build con SERIAL_API_URI.
 */
const SERIAL_API_STAGING = 'https://trazabilidad.staging.educabot.com';
const SERIAL_API_PRODUCTION = 'https://trazabilidad.educabot.com';

// Un pedido al servidor es un viaje de red en medio de una placa recién
// vinculada, con el usuario esperando frente a un modal. Que corte y avise es
// mejor que que se quede colgado.
const REQUEST_TIMEOUT_MS = 10000;

/**
 * Cuántas veces se insiste con la confirmación.
 *
 * Es el único pedido que no se puede perder: el número ya quedó grabado en la
 * placa, así que una confirmación que no llega deja al servidor creyéndolo libre
 * para entregárselo a otra. Insistir angosta esa ventana pero no la cierra; lo
 * que la cierra es que la reserva venza y que la próxima conexión de esa placa
 * vuelva a confirmar, que es algo que hay que pedirle al servidor cuando se
 * definan los endpoints.
 */
const CONFIRM_ATTEMPTS = 3;
const CONFIRM_RETRY_MS = 1000;

/**
 * Cómo salió el pedido. Lo que decide es qué hace la máquina de identidad: sin
 * servidor o sin números la placa se queda sin número de producción y se reporta
 * igual, y un error se puede reintentar.
 */
export const PROVIDER = {
  OK: 'OK',
  // No se pide nada al servidor en este build. Ver isProvisioningEnabled.
  DISABLED: 'DISABLED',
  // El servidor contestó que no tiene números para entregar.
  EXHAUSTED: 'EXHAUSTED',
  ERROR: 'ERROR',
};

/**
 * Por qué se devuelve un número que se había reservado. Viaja en el release para
 * que del otro lado se pueda distinguir un problema de la placa de uno del
 * número que mandaron.
 */
export const RELEASE_REASON = {
  // La placa no lo aceptó o no contestó.
  WRITE_FAILED: 'write_failed',
  // Dijo que lo grabó y la relectura no coincide.
  VERIFY_FAILED: 'verify_failed',
  // No tenía la forma que la placa acepta, así que ni se intentó grabarlo.
  INVALID_FORMAT: 'invalid_format',
  // Se desenchufó la placa, o el usuario cerró todo, en el medio.
  ABORTED: 'aborted',
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * La configuración del build, leída una por una y por su nombre completo.
 *
 * Tiene que ser así y no un process.env[nombre]: lo que pone estos valores en el
 * bundle es un reemplazo de texto en tiempo de build -- DefinePlugin para las
 * variables del entorno y dotenv-webpack para las del .env --, y un reemplazo de
 * texto solo puede encontrar lo que está escrito tal cual. Con el acceso por
 * índice no hay nada que reemplazar: queda una consulta en tiempo de ejecución
 * contra el process de mentira del browser, que está vacío, y toda la
 * configuración se lee como ausente.
 *
 * Se lee en cada llamada y no una vez al cargar el módulo, porque en los tests no
 * hay reemplazo ninguno y cada caso arma su propio entorno.
 */
const envVars = () => {
  try {
    if (typeof process === 'undefined' || !process.env) return {};
    return {
      NODE_ENV: process.env.NODE_ENV,
      SERIAL_API_URI: process.env.SERIAL_API_URI,
      SERIAL_API_TOKEN: process.env.SERIAL_API_TOKEN,
      SERIAL_API_ENABLED: process.env.SERIAL_API_ENABLED,
      SERIAL_API_MOCK: process.env.SERIAL_API_MOCK,
    };
  } catch (error) {
    // Un bundle sin el shim de process no es motivo para romper la vinculación.
    return {};
  }
};

const env = (name) => envVars()[name];

/**
 * Dónde está el servidor. Todavía no sale de cosmos, así que van los dos fijos y
 * manda el entorno del build, con SERIAL_API_URI por encima para poder apuntar a
 * uno local sin tocar el código.
 */
export const serialApiUri = () => {
  const override = env('SERIAL_API_URI');
  if (override) return String(override).replace(/\/+$/, '');
  return (env('NODE_ENV') === 'production') ? SERIAL_API_PRODUCTION : SERIAL_API_STAGING;
};

/**
 * El provider de mentira, que entrega números visiblemente falsos.
 *
 * Va apagado salvo que el build lo encienda a propósito, y no se prende solo
 * cuando falta la URL. Un mock que se activa por omisión terminaría grabando
 * números inventados en la EEPROM de placas de verdad, que es exactamente el
 * dato que este refactor existe para cuidar: quedarían en el sistema como
 * números asignados sin estarlo.
 *
 * Los que entrega arrancan con MOCK justamente para que se noten si alguno se
 * escapa a los datos.
 */
export const isMockEnabled = () => {
  const flag = env('SERIAL_API_MOCK');
  return flag === true || flag === 'true' || flag === '1';
};

/**
 * Si se le pide un número al servidor, o si la placa sin número se reporta así
 * como está.
 *
 * Arranca apagado porque los endpoints todavía no existen: con esto encendido,
 * cada placa sin número pagaría el timeout completo del pedido -- diez segundos
 * con el usuario mirando un spinner -- para terminar igual. Se enciende con
 * SERIAL_API_ENABLED cuando el servidor esté, y ahí conviene invertir el valor
 * por omisión.
 */
export const isProvisioningEnabled = () => {
  if (isMockEnabled()) return true;
  const flag = env('SERIAL_API_ENABLED');
  return flag === true || flag === 'true' || flag === '1';
};

let mockCounter = 0;
// Lo que el mock repartió, para poder mirarlo desde la consola y desde los tests.
export const mockLedger = { reserved: [], confirmed: [], released: [] };

const mockReserve = () => {
  mockCounter += 1;
  const serialString = `MOCK${String(mockCounter).padStart(4, '0')}`;
  mockLedger.reserved.push(serialString);
  logProtocol('', 'Servidor de seriales simulado: número entregado', serialString);
  return { status: PROVIDER.OK, serialString, expiresAt: null };
};

export const resetMockForTests = () => {
  mockCounter = 0;
  mockLedger.reserved = [];
  mockLedger.confirmed = [];
  mockLedger.released = [];
};

const headers = () => {
  const token = env('SERIAL_API_TOKEN');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const post = (path, payload) => axios.post(`${serialApiUri()}${path}`, payload, {
  headers: headers(),
  timeout: REQUEST_TIMEOUT_MS,
});

/**
 * El servidor se quedó sin números para entregar, que no es lo mismo que un
 * error: no hay nada que reintentar y la placa se reporta sin número de
 * producción en vez de quedar trabada pidiendo uno que no va a llegar.
 */
const isExhausted = (error) => error && error.response && error.response.status === 409;

/**
 * Pide un número para una placa que no tiene.
 *
 * El contexto va entero al servidor porque es él quien decide qué número
 * corresponde: de qué placa se trata sale de compilerBoard y del perfil, y el
 * serial de hardware es lo que ata el número entregado a un chip concreto.
 */
export const reserveSerialString = async (context = {}) => {
  if (isMockEnabled()) return mockReserve();
  if (!isProvisioningEnabled()) return { status: PROVIDER.DISABLED };

  try {
    const response = await post('/serials/reserve', context);
    const serialString = response && response.data ? response.data.serialString : null;
    /**
     * Un 200 sin número es un error y no una placa sin número: no hay nada que
     * grabar ni nada que devolver, y tratarlo como "no hay números" escondería
     * un servidor contestando cualquier cosa.
     */
    if (typeof serialString !== 'string' || !serialString) {
      logProtocolError('', 'El servidor de seriales contestó sin número:', response && response.data);
      return { status: PROVIDER.ERROR };
    }
    logProtocol('', 'Número de producción reservado:', serialString);
    return { status: PROVIDER.OK, serialString, expiresAt: (response.data.expiresAt || null) };
  } catch (error) {
    if (isExhausted(error)) {
      logProtocolError('', 'El servidor de seriales no tiene números para entregar.');
      return { status: PROVIDER.EXHAUSTED };
    }
    logProtocolError('', 'No se pudo pedir un número de producción:', error.message);
    return { status: PROVIDER.ERROR };
  }
};

/**
 * Avisa que el número quedó grabado, que es lo que lo pasa a ocupado.
 *
 * Se insiste unas cuantas veces porque es el pedido que no puede perderse: ver
 * CONFIRM_ATTEMPTS.
 */
export const confirmSerialString = async ({ serialString, hardwareSerial = null } = {}, { attempts = CONFIRM_ATTEMPTS, retryMs = CONFIRM_RETRY_MS } = {}) => {
  if (isMockEnabled()) {
    mockLedger.confirmed.push(serialString);
    return { status: PROVIDER.OK };
  }
  if (!isProvisioningEnabled()) return { status: PROVIDER.DISABLED };

  const payload = {
    serialString,
    hardwareSerial,
    verified: true,
    writtenAt: new Date().toISOString(),
  };

  let lastError = null;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      await post('/serials/confirm', payload); // eslint-disable-line no-await-in-loop
      logProtocol('', 'Número de producción confirmado como ocupado:', serialString);
      return { status: PROVIDER.OK };
    } catch (error) {
      lastError = error;
      if (attempt < attempts) await delay(retryMs); // eslint-disable-line no-await-in-loop
    }
  }
  /**
   * Se avisa fuerte porque la placa quedó con un número que el servidor todavía
   * cree libre. No hay nada que deshacer -- borrarlo de la placa sería peor --,
   * así que lo que queda es que se vuelva a confirmar en la próxima conexión.
   */
  logProtocolError('', `El número ${serialString} quedó grabado en la placa pero no se pudo confirmar:`, lastError && lastError.message);
  return { status: PROVIDER.ERROR };
};

/**
 * Devuelve un número que se había reservado y no llegó a grabarse, para que no
 * se queme. El número va en el payload: es lo que identifica cuál devolver.
 */
export const releaseSerialString = async ({ serialString, reason = RELEASE_REASON.ABORTED } = {}) => {
  if (!serialString) return { status: PROVIDER.ERROR };
  if (isMockEnabled()) {
    mockLedger.released.push({ serialString, reason });
    return { status: PROVIDER.OK };
  }
  if (!isProvisioningEnabled()) return { status: PROVIDER.DISABLED };

  try {
    await post('/serials/release', { serialString, reason });
    logProtocol('', 'Número de producción devuelto:', serialString, reason);
    return { status: PROVIDER.OK };
  } catch (error) {
    // Un release que no llega deja el número reservado sin dueño. Lo resuelve el
    // vencimiento de la reserva, así que no se insiste como con la confirmación.
    logProtocolError('', `No se pudo devolver el número ${serialString}:`, error.message);
    return { status: PROVIDER.ERROR };
  }
};

export default reserveSerialString;
